import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Sparkles, Wallet, ArrowRight, CheckCircle2, X, Loader2,
  AlertTriangle, Receipt, Plus, Search, XCircle, Wrench, DollarSign, Shield,
} from "lucide-react";
import { serviceLabel, SERVICE_KEYS } from "@/lib/pricing";
import AutoReloadCard from "@/components/AutoReloadCard";

const TIERS = [
  { id: "bronze",   pct: 5,  label: "Bronze",   sub: "Lower priority" },
  { id: "silver",   pct: 7,  label: "Silver",   sub: "Recommended for most pros", recommended: true },
  { id: "gold",     pct: 9,  label: "Gold",     sub: "Higher priority" },
  { id: "platinum", pct: 12, label: "Platinum", sub: "Highest priority" },
];

const REASON_CODES = [
  { id: "wrong_service_area", label: "Customer outside my service area" },
  { id: "never_submitted",    label: "Customer says they never submitted this" },
];

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function MarketplaceTab() {
  const [settings, setSettings] = useState(null);
  const [cities, setCities] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [topupOpen, setTopupOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/marketplace/settings"),
      api.get("/marketplace/cities"),
      api.get("/marketplace/wallet"),
    ]).then(([s, c, w]) => {
      if (cancelled) return;
      setSettings(s.data);
      setCities(c.data.cities || []);
      setWallet(w.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/marketplace/settings", patch);
      setSettings(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => c.label.toLowerCase().includes(q));
  }, [cities, search]);

  if (loading || !settings) {
    return <div className="text-slate-500 text-sm" data-testid="marketplace-loading">Loading marketplace…</div>;
  }

  const selectedCount = settings.city_slugs.length + settings.custom_zips.length;
  const lowBalance = (wallet?.balance_cents || 0) < (wallet?.low_threshold_cents || 0);
  const noBalance = (wallet?.balance_cents || 0) <= 0;

  return (
    <div className="space-y-5" data-testid="marketplace-tab">
      {/* HERO + EARNINGS EXAMPLE */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0F172A] to-black text-white p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#C75D33]/30 blur-3xl"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C75D33] text-white text-[10px] font-extrabold uppercase tracking-widest mb-3">
            <Sparkles className="w-3 h-3" /> WindowScreenQuote marketplace
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
            Not Angi. Not Yelp. Not a lead-broker auction.
          </h2>
          <p className="text-white/85 text-base mt-3 max-w-2xl leading-relaxed">
            Real homeowners who already saw their <strong>screen quote</strong>. Routed straight to <em>one</em> pro — you.
            You pay a small <strong>%</strong> of <em>their quote</em>, never a flat per-lead fee. Cap <strong>$50</strong>. Never resold.
          </p>
        </div>
      </div>

      {/* DIFFERENTIATION — the "not like the others" pitch */}
      <div className="rounded-3xl bg-white border-2 border-slate-200 p-5 sm:p-7" data-testid="marketplace-differentiator">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#C75D33] mb-1">Built different</div>
        <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          Why screen pros switch off the lead-broker treadmill.
        </h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <DiffPill
            icon={Wrench}
            title="Screens. Only screens."
            desc="We don't sell roofing, HVAC, or kitchen remodel leads on the side. Every homeowner who fills out our funnel is shopping for screen work — not 'thinking about a project.'"
          />
          <DiffPill
            icon={DollarSign}
            title="Pay-by-quote, not pay-per-click."
            desc="A $300 quote charges differently than a $3,000 quote — your wallet hit scales with the job. Capped at $50 even on a $5,000 quote."
          />
          <DiffPill
            icon={Shield}
            title="One company. One lead. No bidding."
            desc="The homeowner sees a quote, and you're the only pro it goes to. No 'sold to 4 contractors,' no race-to-the-bottom voicemail-tag."
          />
          <DiffPill
            icon={XCircle}
            title="No subscriptions. No spam credits."
            desc="Pre-paid wallet, top up when you want. No monthly minimums, no 'use it or lose it' credits, no surprise renewals."
          />
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-2.5 text-xs">
          <div className="flex items-start gap-2 text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>Disputes resolved by a human, not buried behind a chatbot.</span>
          </div>
          <div className="flex items-start gap-2 text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>You set the ZIPs. Leads land in those ZIPs or nowhere.</span>
          </div>
          <div className="flex items-start gap-2 text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>Pause anytime — flip the master switch and nothing routes.</span>
          </div>
        </div>
      </div>

      {/* MASTER ENABLE + WALLET */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <Switch
              checked={settings.enabled}
              onCheckedChange={(v) => save({ enabled: v })}
              data-testid="marketplace-enable"
            />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold text-slate-900">
                {settings.enabled ? "Receiving marketplace leads" : "Marketplace turned off"}
              </div>
              <p className="text-sm text-slate-600 mt-1 leading-snug">
                {settings.enabled
                  ? "We'll route matching leads to you based on your tier, coverage, and filters."
                  : "Flip this on once your coverage and filters are set."}
              </p>
              {settings.enabled && noBalance && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Wallet empty — leads will resume once you top up.
                </div>
              )}
              {settings.enabled && !noBalance && lowBalance && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Low balance — top up to avoid pausing.
                </div>
              )}
            </div>
          </div>
        </div>

        <WalletCard wallet={wallet} onTopup={() => setTopupOpen(true)} />
        <AutoReloadCard />
      </div>

      {/* PRIORITY TIER */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Lead Priority Level</h3>
            <p className="text-sm text-slate-600 mt-0.5">Higher priority gets more leads in competitive zips. Most pros stay at Silver.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {TIERS.map((t) => {
            const active = settings.priority_tier === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => save({ priority_tier: t.id })}
                disabled={saving}
                aria-pressed={active}
                data-testid={`marketplace-tier-${t.id}`}
                data-active={active ? "true" : "false"}
                className={`relative text-left p-4 rounded-2xl border-2 transition-all ${
                  active
                    ? "border-[#0F172A] bg-[#0F172A] text-white shadow-lg"
                    : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
                }`}
              >
                {t.recommended && (
                  <span className={`absolute -top-2 left-3 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    active ? "bg-[#C75D33] text-white" : "bg-[#C75D33] text-white"
                  }`}>Recommended</span>
                )}
                <div className={`text-xs font-bold uppercase tracking-widest ${active ? "text-[#E2A878]" : "text-[#C75D33]"}`}>{t.label}</div>
                <div className="font-display text-3xl font-extrabold mt-1 leading-none">{t.pct}%</div>
                <div className={`text-[11px] mt-2 ${active ? "text-white/75" : "text-slate-500"}`}>{t.sub}</div>
                {active && <CheckCircle2 className="absolute top-3 right-3 w-4 h-4 text-[#E2A878]" />}
              </button>
            );
          })}
        </div>
        <div className="mt-4 text-xs text-slate-500 leading-relaxed">
          You'll be charged <strong>{(TIERS.find((t) => t.id === settings.priority_tier) || TIERS[1]).pct}%</strong> of each quote total, with a <strong>$10 minimum</strong> and <strong>$50 maximum</strong> per lead.
        </div>
      </div>

      {/* COVERAGE — CITIES */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-1">
          <MapPin className="w-5 h-5 text-[#C75D33]" />
          <h3 className="font-display text-lg font-bold text-slate-900">Service Area</h3>
          {selectedCount > 0 && (
            <span className="ml-auto text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#C75D33]/10 text-[#C75D33]" data-testid="marketplace-coverage-count">
              {selectedCount} {selectedCount === 1 ? "area" : "areas"}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600">Pick the cities you serve. We'll route leads from the matching ZIP codes.</p>

        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cities (e.g. Murrieta, Phoenix)"
            className="pl-10 h-11 rounded-xl"
            data-testid="marketplace-city-search"
          />
        </div>

        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto pr-1">
          {filtered.slice(0, 200).map((c) => {
            const checked = settings.city_slugs.includes(c.slug);
            return (
              <label
                key={c.slug}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                  checked ? "border-[#C75D33] bg-[#C75D33]/[0.06]" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`marketplace-city-${c.slug}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => {
                    const next = v
                      ? [...settings.city_slugs, c.slug]
                      : settings.city_slugs.filter((s) => s !== c.slug);
                    update({ city_slugs: next });
                    save({ city_slugs: next });
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.state} · {c.zip_count} ZIP{c.zip_count === 1 ? "" : "s"}</div>
                </div>
              </label>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="mt-4 text-sm text-slate-500">No cities match "{search}". <a href="mailto:support@windowscreenquote.com" className="underline">Ask us to add yours</a>.</div>
        )}

        <AdvancedZips
          custom={settings.custom_zips}
          onChange={(zips) => { update({ custom_zips: zips }); save({ custom_zips: zips }); }}
        />
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Filters</h3>
        <p className="text-sm text-slate-600">Stay focused on the kinds of leads you actually want to bid time on.</p>

        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Services I offer</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {SERVICE_KEYS.map((k) => {
              const checked = settings.services_offered.includes(k);
              return (
                <label
                  key={k}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer ${
                    checked ? "border-[#C75D33] bg-[#C75D33]/[0.06]" : "border-slate-200 bg-white"
                  }`}
                  data-testid={`marketplace-svc-${k}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => {
                      const next = v
                        ? [...settings.services_offered, k]
                        : settings.services_offered.filter((s) => s !== k);
                      update({ services_offered: next });
                      save({ services_offered: next });
                    }}
                  />
                  <span className="text-sm font-semibold text-slate-900">{serviceLabel(k)}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Minimum quote total</div>
          <div className="flex items-center gap-2 max-w-xs">
            <span className="text-slate-500">$</span>
            <Input
              type="number"
              min={0}
              step={25}
              value={Math.round(settings.quote_minimum_cents / 100)}
              onChange={(e) => update({ quote_minimum_cents: Math.max(0, parseInt(e.target.value || "0") * 100) })}
              onBlur={() => save({ quote_minimum_cents: settings.quote_minimum_cents })}
              className="h-11 rounded-xl"
              data-testid="marketplace-quote-min"
              placeholder="e.g. 250"
            />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Only send me leads above this quote total. Set to 0 to accept any size.</p>
        </div>
      </div>

      <TopupDialog open={topupOpen} onOpenChange={setTopupOpen} />
    </div>
  );
}

function WalletCard({ wallet, onTopup }) {
  if (!wallet) return null;
  const empty = wallet.balance_cents <= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="marketplace-wallet-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33]">Wallet balance</div>
          <div className="font-display text-4xl font-extrabold text-slate-900 mt-1 leading-none" data-testid="marketplace-wallet-balance">
            {dollars(wallet.balance_cents)}
          </div>
          <div className="text-xs text-slate-500 mt-1.5">{empty ? "Top up to start receiving leads." : "Drained as leads are delivered."}</div>
        </div>
        <Wallet className={`w-7 h-7 ${empty ? "text-amber-500" : "text-emerald-500"}`} />
      </div>
      <Button
        onClick={onTopup}
        className="w-full h-11 mt-4 rounded-xl bg-[#0F172A] hover:bg-black text-sm font-bold"
        data-testid="marketplace-wallet-topup-btn"
      >
        <Plus className="w-4 h-4 mr-1.5" /> Top up wallet
      </Button>
      {wallet.transactions && wallet.transactions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <Receipt className="w-3 h-3" /> Recent activity
          </div>
          <ul className="space-y-1.5 max-h-44 overflow-y-auto">
            {wallet.transactions.slice(0, 6).map((t) => (
              <li key={t.id} className="flex items-center justify-between text-xs">
                <span className="text-slate-600 truncate pr-3">{t.note || t.kind}</span>
                <span className={`font-bold shrink-0 ${t.amount_cents >= 0 ? "text-emerald-600" : "text-slate-900"}`}>
                  {t.amount_cents >= 0 ? "+" : "−"}{dollars(Math.abs(t.amount_cents))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AdvancedZips({ custom, onChange }) {
  const [open, setOpen] = useState(custom.length > 0);
  const [raw, setRaw] = useState(custom.join(", "));
  useEffect(() => { setRaw(custom.join(", ")); }, [custom]);

  const commit = () => {
    const zips = raw.split(/[\s,]+/).map((z) => z.trim()).filter((z) => /^\d{5}$/.test(z));
    onChange(Array.from(new Set(zips)));
  };

  return (
    <div className="mt-5 pt-5 border-t border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-800"
        data-testid="marketplace-advanced-toggle"
      >
        {open ? "Hide" : "Add"} specific ZIP codes (advanced)
      </button>
      {open && (
        <div className="mt-3">
          <Input
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onBlur={commit}
            placeholder="92562, 92563, 92591…"
            className="h-11 rounded-xl"
            data-testid="marketplace-custom-zips"
          />
          <p className="text-[11px] text-slate-500 mt-1.5">Comma- or space-separated 5-digit ZIPs. Use this to fine-tune beyond city boundaries.</p>
        </div>
      )}
    </div>
  );
}

function TopupDialog({ open, onOpenChange }) {
  const [submitting, setSubmitting] = useState(false);
  const start = async (pack) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/marketplace/wallet/topup", { pack, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] rounded-3xl" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Top up your wallet</DialogTitle>
          <DialogDescription>Pick a starter pack. Funds land instantly after checkout and never expire.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-2">
          {[
            { pack: "100", label: "$100", sub: "~2–10 leads", testid: "topup-100" },
            { pack: "250", label: "$250", sub: "~5–25 leads", testid: "topup-250", best: true },
            { pack: "500", label: "$500", sub: "~10–50 leads", testid: "topup-500" },
          ].map((p) => (
            <button
              key={p.pack}
              onClick={() => start(p.pack)}
              disabled={submitting}
              data-testid={p.testid}
              className={`relative w-full text-left px-5 py-4 rounded-2xl border-2 transition-all ${
                p.best
                  ? "border-[#0F172A] bg-[#0F172A] text-white"
                  : "border-slate-200 bg-white hover:border-slate-400"
              }`}
            >
              {p.best && <span className="absolute -top-2 right-4 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#C75D33] text-white">Most popular</span>}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-3xl font-extrabold leading-none">{p.label}</div>
                  <div className={`text-xs mt-1.5 ${p.best ? "text-white/75" : "text-slate-500"}`}>{p.sub}</div>
                </div>
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Lead dispute trigger ---------- */
export function LeadDisputeButton({ lead, onResolved }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!lead?.marketplace?.assignment_id) return null;

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setSubmitting(true);
    try {
      await api.post(`/marketplace/leads/${lead.id}/dispute`, { reason, detail });
      toast.success("Credit request submitted. We'll review within 1 business day.");
      setOpen(false);
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit dispute");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-slate-500 hover:text-[#C75D33] underline font-semibold"
        data-testid="lead-dispute-btn"
      >
        Request credit
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)] rounded-3xl" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Request a credit</DialogTitle>
            <DialogDescription>7-day window. Credits are reviewed manually for now (auto-credits come once call/SMS logging is live).</DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-2">
            {REASON_CODES.map((r) => (
              <label
                key={r.id}
                className={`flex items-start gap-2.5 px-3 py-3 rounded-xl border cursor-pointer ${
                  reason === r.id ? "border-[#C75D33] bg-[#C75D33]/[0.06]" : "border-slate-200"
                }`}
                data-testid={`dispute-reason-${r.id}`}
              >
                <input type="radio" name="reason" className="mt-1" checked={reason === r.id} onChange={() => setReason(r.id)} />
                <span className="text-sm font-semibold text-slate-800">{r.label}</span>
              </label>
            ))}
          </div>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Anything we should know? (call timestamps, customer reply, etc.)"
            className="w-full mt-3 px-3 py-2 rounded-xl border border-slate-200 text-sm h-24 resize-none"
            data-testid="dispute-detail"
          />
          <Button
            onClick={submit}
            disabled={submitting || !reason}
            className="w-full h-11 mt-2 rounded-xl bg-[#0F172A] hover:bg-black text-sm font-bold"
            data-testid="dispute-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : "Submit credit request"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}


function DiffPill({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100" data-testid={`diff-pill-${title.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
      <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#C75D33]" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-slate-900 leading-tight">{title}</div>
        <div className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
